import { el, text } from './walkout.js';
import { STATS, STYLES } from '../core/styles.js';

/**
 * One style, said out loud.
 *
 * The create screen has said what a style does before you take it since step
 * 67 — the four multipliers as the plus and minus they are, the moves it
 * teaches, and `ko.describe`, the file's own sentence about the only kind of
 * moment that can finish somebody.
 *
 * From step 70 the **gym** sells the second and third styles, and it has to say
 * the same things: choosing a style at creation and buying one later are the
 * same decision made with different money, and a shop that showed less than the
 * character creator would be asking somebody to spend twenty-four points on a
 * name. So this is one component and two callers, for the reason `promoter.js`
 * is: a second copy of it would be the one that drifts, and the gym is exactly
 * where it would drift *quietly*.
 *
 * The one difference between the two callers is the `ko` line. On the create
 * screen the style being looked at **is** the primary and its condition is how
 * you will win. In the gym it is a secondary, it changes nothing about how you
 * win, and saying "wins by" there would be a lie — so the caller says which it
 * is and the card says the true thing.
 */
export function styleCard(id, { role = 'primary', cost = null, owned = false } = {}) {
  const style = STYLES.styles[id];
  if (!style) return null;

  const root = el('button', 'style-card ui-panel');
  root.type = 'button';
  root.dataset.style = id;
  root.dataset.role = role;

  /** The slot a caller writes its own marker into — a pick order, or a price. */
  const badge = text('div', 'style-card__badge', cost === null ? '' : `${cost}p`);
  const name = text('div', 'style-card__name', style.name);

  const stats = el('div', 'style-card__stats');
  for (const stat of Object.keys(STATS)) {
    const value = style.stats[stat] ?? 1;
    const row = el('div', 'style-card__stat');
    row.dataset.tone = value > 1.001 ? 'up' : value < 0.999 ? 'down' : 'flat';
    row.append(
      text('span', 'style-card__stat-name', stat),
      // The number the style actually applies, as the thing it is: a deviation.
      text('span', 'style-card__stat-value', `${value > 1 ? '+' : ''}${Math.round((value - 1) * 100)}%`)
    );
    stats.append(row);
  }

  const moves = text('div', 'style-card__moves', style.moves.join(' · '));

  const ko = el('div', 'style-card__ko');
  ko.append(
    text('span', 'ui-label', role === 'primary' ? 'wins by' : 'its finish'),
    text(
      'span',
      '',
      role === 'primary'
        ? style.ko.describe
        : `${style.ko.describe} — not yours, a second style does not move it`
    )
  );

  // Both wrap rather than truncate, so these are belt and braces for a move
  // list longer than any style currently teaches.
  moves.title = style.moves.join(' · ');
  ko.title = style.ko.describe;

  root.append(badge, name, stats, moves, ko);
  if (owned) root.dataset.owned = 'true';
  return root;
}

/**
 * What a secondary is actually worth, in the terms the stats are in.
 *
 * `weights.secondary` is 0.5, so a style taken second contributes **half** its
 * deviation. The gym says so rather than showing the full numbers and letting
 * somebody work it out, because the full numbers are the ones on the card and
 * the difference between them is the whole of what "secondary" means.
 */
export function secondaryNote(config = STYLES) {
  // "a bought style", not "a second": the same line is shown when the third is
  // on offer, and the weighting is the same for both.
  return `a bought style is worth ${Math.round(config.weights.secondary * 100)}% of these`;
}
